import React, { useState, useMemo, useEffect } from 'react';
import {
  XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, AreaChart, Area
} from 'recharts';
import {
  Mountain, Plus, Trash2, Sun, Moon,
  ArrowDownCircle, AlertCircle,
  TrendingDown, Table, Calendar,
  Clock, MinusCircle, Sparkles, AlertTriangle,
  ChevronUp, ChevronDown, Download, CheckCircle2
} from 'lucide-react';

const STORAGE_KEY = 'debt_mountain_v18';
const THEME_KEY = 'debt_mountain_theme_v18';

// Distinct palette that stays legible past 5 loans — kept separate from the
// Alpine Dusk brand palette on purpose: it needs 8 mutually-distinguishable
// hues, which the 1-accent brand palette can't supply without hurting
// legibility. See the 2026-08 persona review for the full reasoning.
const LOAN_COLORS = [
  '#6366f1', '#ec4899', '#14b8a6', '#f59e0b',
  '#8b5cf6', '#10b981', '#ef4444', '#3b82f6',
];
const getColor = (i) => LOAN_COLORS[i % LOAN_COLORS.length];

// Single localStorage read with error handling
const getSaved = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const initialLoans = [
  { id: "L1", name: "Chase Sapphire Card",    balance: 4850.00,  rate: 24.99, minPayment: 145 },
  { id: "L2", name: "Auto Loan (SUV)",         balance: 22400.00, rate:  5.25, minPayment: 460 },
  { id: "L3", name: "Federal Student Loan",    balance: 31200.00, rate:  6.80, minPayment: 325 },
  { id: "L4", name: "Home Improvement Loan",   balance: 8500.00,  rate: 11.50, minPayment: 210 },
  { id: "L5", name: "Local Credit Union CC",   balance: 1200.00,  rate: 18.25, minPayment:  40 },
];

const formatCurrency = (val, forceDecimals = true) => {
  const num = Math.max(0, Number(val) || 0);
  const hasDecimals = num % 1 !== 0;
  return num.toLocaleString(undefined, {
    minimumFractionDigits: (forceDecimals || hasDecimals) ? 2 : 0,
    maximumFractionDigits: 2,
  });
};

const formatRate = (val) => (Number(val) || 0).toFixed(1);

const App = () => {
  const [loans, setLoans]               = useState(() => getSaved().loans         ?? initialLoans);
  const [monthlyBudget, setMonthlyBudget] = useState(() => getSaved().monthlyBudget ?? 2500);
  const [view, setView]                 = useState(() => getSaved().view          ?? 'table');
  const [strategy, setStrategy]         = useState(() => getSaved().strategy      ?? 'avalanche');
  const [isDarkMode, setIsDarkMode]     = useState(() => {
    try {
      const saved = localStorage.getItem(THEME_KEY);
      return saved === null ? true : saved === 'true';
    } catch { return true; }
  });

  // Captured once at mount — keeps all date math consistent for the session
  const [baseDate] = useState(() => new Date());

  const [editingCell, setEditingCell] = useState({ id: null, field: null, value: '' });
  const [sortConfig, setSortConfig]   = useState({ key: null, direction: 'asc' });

  useEffect(() => {
    document.title = "Debt Mountain | Payment Planner";
    const link = document.querySelector("link[rel~='icon']") || document.createElement('link');
    link.rel  = 'icon';
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#3E5C76" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m8 3 4 8 5-5 5 15H2L8 3z"/></svg>`;
    link.href = `data:image/svg+xml,${encodeURIComponent(svg)}`;
    document.getElementsByTagName('head')[0].appendChild(link);
  }, []);

  // Persist everything — including strategy
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ loans, monthlyBudget, view, strategy }));
    localStorage.setItem(THEME_KEY, isDarkMode.toString());
  }, [loans, monthlyBudget, isDarkMode, view, strategy]);

  const totalDebtBalance = useMemo(() => loans.reduce((sum, l) => sum + (Number(l.balance) || 0), 0), [loans]);
  const totalMinPayment  = useMemo(() => loans.reduce((sum, l) => sum + (Number(l.minPayment) || 0), 0), [loans]);

  const sortedLoans = useMemo(() => {
    const copy = [...loans];
    if (sortConfig.key !== null) {
      copy.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];
        if (typeof aVal === 'number') return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
        aVal = (aVal || '').toString().toLowerCase();
        bVal = (bVal || '').toString().toLowerCase();
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ?  1 : -1;
        return 0;
      });
    }
    return copy;
  }, [loans, sortConfig]);

  const requestSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const handleBudgetChange = (e) => {
    const clean = e.target.value.replace(/[^0-9.]/g, '');
    const parts = clean.split('.');
    const sanitized = parts[0] + (parts.length > 1 ? '.' + parts[1].slice(0, 2) : '');
    setEditingCell(prev => ({ ...prev, value: sanitized }));
    const num = parseFloat(sanitized);
    if (!isNaN(num)) setMonthlyBudget(num);
  };

  const handleInputBlur = (id, field) => {
    const raw = editingCell.value.replace(/[^0-9.]/g, '');
    const finalValue = field === 'name' ? editingCell.value : Math.max(0, parseFloat(raw) || 0);
    if (id === 'budget') {
      setMonthlyBudget(finalValue);
    } else {
      setLoans(prev => prev.map(l => l.id === id ? { ...l, [field]: finalValue } : l));
    }
    setEditingCell({ id: null, field: null, value: '' });
  };

  const handleFocus = (e, id, field, value) => {
    setEditingCell({ id, field, value: field === 'name' ? value : value.toString() });
    // Deferred one tick so React can swap the field's displayed value from
    // the formatted string to the raw editable one before we select it.
    // Guarding on activeElement matters: .select() forces focus onto its
    // target, so if focus already moved on by the time this fires (e.g. an
    // OS-level focus flicker from alt-tabbing to copy a value, then back to
    // paste) it would otherwise yank focus back and re-fire onFocus here,
    // queuing another select() — a self-sustaining focus/blur loop.
    setTimeout(() => {
      if (e.target && document.activeElement === e.target) e.target.select();
    }, 0);
  };

  // Enter commits the edit; Tab moves naturally
  const handleEnter = (e) => { if (e.key === 'Enter') e.target.blur(); };

  // If the clipboard holds a copied spreadsheet range (tab/newline-delimited),
  // pasting it as-is into a single field would insert the raw tab characters
  // as literal text. Keep only the first cell's value instead.
  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text').split(/\r?\n/)[0].split('\t')[0];
    setEditingCell(prev => ({ ...prev, value: text }));
  };

  const projection = useMemo(() => {
    if (totalDebtBalance === 0 || monthlyBudget <= 0)
      return { history: [], totalMonths: 0, totalPaidInterest: 0, payoffMonths: {} };

    let current = loans.map(l => ({
      id:         l.id,
      balance:    Math.max(0, Number(l.balance) || 0),
      rate:       Number(l.rate) || 0,
      min:        Number(l.minPayment) || 0,
      isFinished: false,
    }));

    const history      = [];
    const payoffMonths = {};
    let month          = 0;
    let totalPaidInterest = 0;

    history.push({
      name: baseDate.toLocaleString('default', { month: 'short', year: 'numeric' }),
      ...Object.fromEntries(current.map(l => [l.id, l.balance])),
    });

    while (current.some(l => l.balance > 0) && month < 600) {
      month++;
      const date = new Date(baseDate);
      date.setMonth(date.getMonth() + month);

      for (const l of current) {
        if (l.balance > 0) {
          const interest = l.balance * (l.rate / 100) / 12;
          l.balance     += interest;
          totalPaidInterest += interest;
        }
      }

      let pool = monthlyBudget;
      current.forEach(l => {
        if (l.balance > 0) {
          const payment = Math.min(l.balance, l.min);
          l.balance -= payment;
          pool      -= payment;
        }
      });

      if (pool > 0) {
        const targets = [...current]
          .filter(l => l.balance > 0)
          .sort((a, b) => strategy === 'avalanche' ? b.rate - a.rate : a.balance - b.balance);
        for (const t of targets) {
          const payment = Math.min(t.balance, pool);
          t.balance -= payment;
          pool      -= payment;
          if (pool <= 0) break;
        }
      }

      const monthData = { name: date.toLocaleString('default', { month: 'short', year: 'numeric' }) };
      for (const l of current) {
        if (l.balance < 0.01) l.balance = 0;
        if (l.balance === 0) {
          if (!l.isFinished) {
            monthData[l.id] = 0;
            l.isFinished    = true;
            payoffMonths[l.id] = month;
          } else {
            monthData[l.id] = null;
          }
        } else {
          monthData[l.id] = parseFloat(l.balance.toFixed(2));
        }
      }
      history.push(monthData);
    }

    return { history, totalMonths: month, totalPaidInterest, payoffMonths };
  }, [loans, monthlyBudget, strategy, totalDebtBalance, baseDate]);

  const freedomDate = useMemo(() => {
    const d = new Date(baseDate);
    d.setMonth(d.getMonth() + projection.totalMonths);
    return { month: d.toLocaleString('default', { month: 'long' }), year: d.getFullYear() };
  }, [projection.totalMonths, baseDate]);

  const payoffDates = useMemo(() => {
    const result = {};
    loans.forEach(l => {
      const m = projection.payoffMonths[l.id];
      if (m !== undefined) {
        const d = new Date(baseDate);
        d.setMonth(d.getMonth() + m);
        result[l.id] = d.toLocaleString('default', { month: 'short', year: 'numeric' });
      }
    });
    return result;
  }, [loans, projection.payoffMonths, baseDate]);

  const isBelowMinimum = monthlyBudget < totalMinPayment;
  const surplusAmount  = Math.max(0, monthlyBudget - totalMinPayment);
  const shortfallAmount = Math.max(0, totalMinPayment - monthlyBudget);
  const sliderMax      = Math.max(10000, Math.ceil((totalMinPayment * 2) / 1000) * 1000);
  // At $0/mo the simulator bails out immediately (see the projection
  // useMemo's early return), leaving totalMonths at its default 0 — which
  // reads as "paid off this month" rather than "will never pay off". Needs
  // its own copy everywhere totalMonths normally drives the display.
  const noProgress     = monthlyBudget <= 0 && totalDebtBalance > 0;

  // A loan only reads as "paid off" once it's genuinely been filled in and
  // zeroed — guards against a freshly-added blank row (balance/rate/min all
  // 0 by default) briefly flashing a celebration it hasn't earned.
  const isPaidOff = (l) => Number(l.balance) <= 0 && (Number(l.rate) > 0 || Number(l.minPayment) > 0);
  const paidOffCount = loans.filter(isPaidOff).length;

  const handleReset = () => {
    if (window.confirm('Reset all loans and budget to the sample data?')) {
      setLoans(initialLoans);
      setMonthlyBudget(2500);
    }
  };

  const handleExport = () => {
    const headers = ['Debt', 'Balance', 'Rate (%)', 'Min Payment', 'Pays Off'];
    const rows = loans.map(l => [
      `"${String(l.name).replace(/"/g, '""')}"`,
      Number(l.balance).toFixed(2),
      Number(l.rate).toFixed(2),
      Number(l.minPayment).toFixed(2),
      payoffDates[l.id] ?? 'N/A',
    ]);
    const csv  = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = 'debt-mountain.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const theme = {
    bg:    isDarkMode ? 'bg-[#0E1620]'    : 'bg-[#F7F5F1]',
    card:  isDarkMode ? 'bg-[#172431] border-slate-800' : 'bg-white border-slate-200',
    text:  isDarkMode ? 'text-slate-200'  : 'text-slate-800',
    // Replaces the old flat slate-400/slate-500 labels, which failed WCAG
    // contrast in one mode or the other (slate-500 on dark ≈4.02:1, slate-400
    // on light ≈2.45:1 — both below the 4.5:1 AA floor). This pairing passes
    // comfortably in both modes.
    muted: isDarkMode ? 'text-slate-400' : 'text-slate-600',
    // Alpine Dusk accent/success/danger roles. Each needs a light-mode and a
    // dark-mode shade because no single shade clears 4.5:1 text contrast
    // against both the near-black and near-white page backgrounds (verified
    // by hand — e.g. primary-600 is 6.4:1 on the light bg but only 2.6:1 on
    // the dark one). Each variant is written out in full so Tailwind's JIT
    // scanner can find the literal class name — a runtime-built class string
    // like `text-${x}` would silently fail to generate any CSS.
    accent:            isDarkMode ? 'text-primary-400'        : 'text-primary-600',
    accentHover:       isDarkMode ? 'hover:text-primary-400'  : 'hover:text-primary-600',
    accentBorder:      isDarkMode ? 'border-primary-400'      : 'border-primary-600',
    accentBorderHover: isDarkMode ? 'hover:border-primary-400/50' : 'hover:border-primary-600/50',
    success:           isDarkMode ? 'text-summit-500'         : 'text-summit-700',
    danger:            isDarkMode ? 'text-terracotta-400'     : 'text-terracotta-600',
    dangerHover:       isDarkMode ? 'hover:text-terracotta-400' : 'hover:text-terracotta-600',
    dangerBorderHover: isDarkMode ? 'hover:border-terracotta-400/50' : 'hover:border-terracotta-600/50',
  };

  const SortIcon = ({ column }) => {
    if (sortConfig.key !== column) return null;
    return sortConfig.direction === 'asc'
      ? <ChevronUp size={12} className="ml-1 inline" />
      : <ChevronDown size={12} className="ml-1 inline" />;
  };

  // One strategy control, used in both the Ledger toolbar and the Graph view —
  // previously two different implementations (an 8px "click to cycle" pill in
  // Ledger vs. a proper 9px segmented pair in Graph) for the same setting.
  // `hint` shows the psychological tradeoff as visible copy (used in Graph,
  // where there's room); the compact Ledger toolbar relies on the title
  // tooltip for the same information.
  const StrategyToggle = ({ hint = false }) => (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-3">
        <span className={`text-label font-black uppercase tracking-widest ${theme.muted}`}>Strategy:</span>
        <div className={`flex items-center gap-1 p-1 rounded-xl border ${theme.card}`}>
          {['avalanche', 'snowball'].map(s => (
            <button
              key={s}
              onClick={() => setStrategy(s)}
              title={s === 'avalanche' ? 'Saves the most on interest' : 'Clears loans faster, which is often easier to stick with'}
              className={`text-xs font-black uppercase tracking-widest px-3 py-2 rounded-lg transition-all active:scale-95 ${
                strategy === s ? 'bg-primary-600 text-white shadow-lg shadow-primary-600/30' : `${theme.muted} ${theme.accentHover}`
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>
      {hint && (
        <p className={`text-label ${theme.muted} max-w-xs`}>
          {strategy === 'avalanche'
            ? 'Saves the most money on interest.'
            : 'Clears loans faster to keep you motivated.'}
        </p>
      )}
    </div>
  );

  return (
    <div className={`min-h-screen ${theme.bg} ${theme.text} font-sans transition-colors duration-300`}>
      <div className="max-w-6xl mx-auto p-6 md:p-10 flex flex-col gap-8">

        <header className="flex justify-between items-start">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-primary-600 flex items-center justify-center text-white shadow-xl shadow-primary-600/30">
              <Mountain size={28} />
            </div>
            <div>
              <h1 className={`text-2xl font-black font-serif italic tracking-tight leading-none ${theme.accent}`}>Debt Mountain</h1>
              <p className={`${theme.muted} text-[11px] font-bold tracking-[0.2em] uppercase mt-1`}>Find your way down</p>
            </div>
          </div>
          <button onClick={() => setIsDarkMode(m => !m)} className={`h-12 w-12 flex items-center justify-center rounded-2xl border ${theme.card} hover:scale-105 transition-all`}>
            {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
          </button>
        </header>

        <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className={`${theme.card} p-5 rounded-2xl border shadow-sm`}>
            <div className={`flex items-center gap-2 mb-1.5 ${theme.muted}`}>
              <ArrowDownCircle size={14} />
              <span className="text-label font-semibold uppercase tracking-widest">Total Balance</span>
              {paidOffCount > 0 && (
                <span className={`ml-auto flex items-center gap-1 text-label font-bold ${theme.success}`}>
                  <CheckCircle2 size={12} /> {paidOffCount} paid off
                </span>
              )}
            </div>
            <p className="text-lg font-bold tabular-nums">${formatCurrency(totalDebtBalance)}</p>
          </div>
          <div className={`${theme.card} p-5 rounded-2xl border shadow-sm`}>
            <div className={`flex items-center gap-2 mb-1.5 ${theme.muted}`}>
              <AlertCircle size={14} />
              <span className="text-label font-semibold uppercase tracking-widest">Min Payments</span>
            </div>
            <p className="text-lg font-bold tabular-nums">${formatCurrency(totalMinPayment)}</p>
          </div>
          <div className={`${theme.card} p-5 rounded-2xl border shadow-sm`}>
            <div className={`flex items-center gap-2 mb-1.5 ${theme.accent}`}>
              <MinusCircle size={14} />
              <span className="text-label font-semibold uppercase tracking-widest">Interest Payable</span>
            </div>
            <p className={`text-lg font-bold tabular-nums ${theme.accent}`}>
              {projection.totalMonths >= 600 ? "N/A" : `$${formatCurrency(projection.totalPaidInterest)}`}
            </p>
            {projection.totalMonths >= 600 && (
              <p className={`text-label font-bold uppercase tracking-wider mt-1 ${theme.muted}`}>
                Exceeds 600-month limit
              </p>
            )}
          </div>
          <div className="bg-gradient-to-br from-primary-600 to-[#25384a] p-5 rounded-2xl shadow-xl shadow-primary-600/20 text-white relative overflow-hidden">
            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-1.5 opacity-80">
                <Calendar size={14} />
                <span className="text-label font-black uppercase tracking-widest">Zero Balance</span>
              </div>
              <p className="text-2xl font-black tabular-nums">
                {noProgress ? "N/A" : projection.totalMonths >= 600 ? "Over 50 yrs" : `${freedomDate.month} ${freedomDate.year}`}
              </p>
              <div className="flex items-center gap-2 mt-1 opacity-70">
                <Clock size={11} />
                <p className="text-label font-bold uppercase tracking-wider">
                  {noProgress ? "No monthly payment set" : projection.totalMonths >= 600 ? "Exceeds 600-month limit" : `${projection.totalMonths} months`}
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className={`${theme.card} rounded-2xl p-6 md:px-10 md:py-6 border shadow-lg overflow-hidden`}>
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-0.5">
              <span className={`text-label font-black uppercase tracking-[0.2em] ${theme.muted}`}>Monthly Payment Plan</span>
              <div className="flex items-center gap-4 flex-wrap">
                <div className={`flex items-baseline gap-1.5 transition-colors duration-300 ${isBelowMinimum ? theme.danger : theme.accent}`}>
                  <span className="text-3xl font-bold">$</span>
                  <input
                    type="text"
                    className="bg-transparent text-3xl font-bold tabular-nums outline-none border-b-2 border-transparent focus:border-current transition-all placeholder-slate-300 w-auto min-w-[100px]"
                    style={{ width: `${(editingCell.id === 'budget' ? editingCell.value.length : formatCurrency(monthlyBudget, false).length) + 1}ch` }}
                    value={editingCell.id === 'budget' ? editingCell.value : formatCurrency(monthlyBudget, false)}
                    onFocus={(e) => handleFocus(e, 'budget', 'budget', monthlyBudget)}
                    onBlur={() => handleInputBlur('budget', 'budget')}
                    onChange={handleBudgetChange}
                    onKeyDown={handleEnter}
                    spellCheck="false"
                  />
                </div>
                <div className="flex items-center">
                  {isBelowMinimum ? (
                    <div className={`flex items-center gap-2 px-3 py-1 bg-terracotta-500/10 border border-terracotta-500/20 rounded-full ${theme.danger}`}>
                      <AlertTriangle size={12} />
                      <span className="text-label font-black uppercase tracking-widest">${formatCurrency(shortfallAmount)} Below Minimums</span>
                    </div>
                  ) : surplusAmount > 0 ? (
                    <div className={`flex items-center gap-2 px-3 py-1 bg-summit-500/10 border border-summit-500/20 rounded-full ${theme.success}`}>
                      <Sparkles size={12} />
                      <span className="text-label font-black uppercase tracking-widest">${formatCurrency(surplusAmount)} Surplus</span>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
            <div className="w-full">
              <input
                type="range"
                min={0} max={sliderMax} step={1}
                value={monthlyBudget}
                onChange={(e) => setMonthlyBudget(parseFloat(e.target.value))}
                className={`w-full h-1.5 rounded-full appearance-none cursor-pointer transition-colors duration-300 ${isBelowMinimum ? 'bg-terracotta-500/20 accent-terracotta-500' : 'bg-slate-800/10 dark:bg-slate-800/50 accent-primary-600'} minimal-slider`}
              />
            </div>
          </div>
        </section>

        <div className={`${theme.card} rounded-2xl border overflow-hidden shadow-2xl min-h-[450px] flex flex-col`}>
          <div className="flex items-center border-b border-inherit">
            <button
              onClick={() => setView('table')}
              className={`flex-1 py-4 flex items-center justify-center gap-3 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500/60 ${view === 'table' ? `bg-primary-600/5 ${theme.accent} border-b-2 ${theme.accentBorder}` : `${theme.muted} hover:text-slate-300`}`}
            >
              <Table size={14} />
              <span className="text-label font-black uppercase tracking-widest">Ledger</span>
            </button>
            <button
              onClick={() => setView('graph')}
              className={`flex-1 py-4 flex items-center justify-center gap-3 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500/60 ${view === 'graph' ? `bg-primary-600/5 ${theme.accent} border-b-2 ${theme.accentBorder}` : `${theme.muted} hover:text-slate-300`}`}
            >
              <TrendingDown size={14} />
              <span className="text-label font-black uppercase tracking-widest">Projection Graph</span>
            </button>
          </div>

          <div className="p-6 md:p-8 flex-1">
            {view === 'table' ? (
              <div className="flex flex-col">
                <div className="flex items-center mb-6 px-2 flex-wrap gap-4">
                  <button
                    onClick={() => setLoans(prev => [{ id: `L${Date.now()}`, name: "New Debt", balance: 0, rate: 0, minPayment: 0 }, ...prev])}
                    className="flex items-center gap-2 bg-primary-600 hover:bg-primary-500 text-white px-4 py-2 rounded-xl font-bold text-label uppercase tracking-widest shadow-lg transition-all"
                  >
                    <Plus size={12} /> Add Debt
                  </button>
                  <button
                    onClick={handleExport}
                    disabled={loans.length === 0}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-label uppercase tracking-widest border transition-all ${theme.card} ${theme.muted} ${theme.accentHover} ${theme.accentBorderHover} disabled:opacity-40 disabled:cursor-not-allowed`}
                  >
                    <Download size={12} /> Export CSV
                  </button>
                  <button
                    onClick={handleReset}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-label uppercase tracking-widest border transition-all ${theme.card} ${theme.muted} ${theme.dangerHover} ${theme.dangerBorderHover}`}
                  >
                    Reset
                  </button>
                  <div className="ml-auto">
                    <StrategyToggle />
                  </div>
                </div>

                {loans.length === 0 ? (
                  <div className="py-16 text-center">
                    <p className={`${theme.muted} text-sm font-bold`}>No debts added.</p>
                    <p className={`${theme.muted} text-xs mt-1`}>Click "Add Debt" to get started, or Reset to restore sample data.</p>
                  </div>
                ) : (
                  <>
                    {/* Mobile cards */}
                    <div className="flex flex-col gap-3 md:hidden">
                      {sortedLoans.map(loan => {
                        const monthlyInterest = (Number(loan.balance) * Number(loan.rate) / 100) / 12;
                        const minBelowInterest = Number(loan.balance) > 0 && Number(loan.rate) > 0 && Number(loan.minPayment) <= monthlyInterest && !payoffDates[loan.id];
                        const payoffDate = payoffDates[loan.id];
                        const paidOff = isPaidOff(loan);
                        return (
                          <div key={loan.id} className={`${theme.card} rounded-2xl border p-4 flex flex-col gap-3 ${paidOff ? 'border-summit-500/50' : ''}`}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-1.5 flex-1">
                                {minBelowInterest && <AlertTriangle size={11} className={`${theme.danger} shrink-0`} />}
                                <input
                                  className="bg-transparent font-bold text-sm outline-none w-full"
                                  value={editingCell.id === loan.id && editingCell.field === 'name' ? editingCell.value : loan.name}
                                  onFocus={(e) => handleFocus(e, loan.id, 'name', loan.name)}
                                  onBlur={() => handleInputBlur(loan.id, 'name')}
                                  onChange={(e) => setEditingCell({ ...editingCell, value: e.target.value })}
                                  onKeyDown={handleEnter}
                                  onPaste={handlePaste}
                                />
                              </div>
                              <button onClick={() => setLoans(prev => prev.filter(l => l.id !== loan.id))} className={`text-slate-600 ${theme.dangerHover} -m-2 p-2 rounded-lg active:scale-95 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500/60`}>
                                <Trash2 size={14} />
                              </button>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              {[
                                ['Balance',     'balance',    `$${formatCurrency(loan.balance)}`],
                                ['Rate',        'rate',       `${formatRate(loan.rate)}%`],
                                ['Min Payment', 'minPayment', `$${formatCurrency(loan.minPayment)}`],
                              ].map(([label, field, display]) => (
                                <div key={field}>
                                  <p className={`text-label font-black uppercase tracking-widest ${theme.muted} mb-0.5`}>{label}</p>
                                  <input
                                    className={`bg-transparent font-bold text-sm outline-none w-full ${field === 'rate' ? theme.accent : ''}`}
                                    value={editingCell.id === loan.id && editingCell.field === field ? editingCell.value : display}
                                    onFocus={(e) => handleFocus(e, loan.id, field, loan[field])}
                                    onBlur={() => handleInputBlur(loan.id, field)}
                                    onChange={(e) => setEditingCell({ ...editingCell, value: e.target.value })}
                                    onKeyDown={handleEnter}
                                    onPaste={handlePaste}
                                  />
                                </div>
                              ))}
                              <div>
                                <p className={`text-label font-black uppercase tracking-widest ${theme.muted} mb-0.5`}>Pays Off</p>
                                {paidOff ? (
                                  <p className={`flex items-center gap-1 font-bold text-sm ${theme.success}`}>
                                    <CheckCircle2 size={13} /> Paid off
                                  </p>
                                ) : (
                                  <p className={`font-bold text-sm ${theme.muted}`}>{payoffDate ?? <span className={theme.danger}>N/A</span>}</p>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Desktop table */}
                    <div className="hidden md:block overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className={`text-left text-label font-black uppercase tracking-[0.2em] ${theme.muted} border-b border-inherit`}>
                            <th className="py-4 pl-2 cursor-pointer whitespace-nowrap" onClick={() => requestSort('name')}>Debt <SortIcon column="name" /></th>
                            <th className="py-4 cursor-pointer whitespace-nowrap"       onClick={() => requestSort('balance')}>Balance <SortIcon column="balance" /></th>
                            <th className="py-4 cursor-pointer whitespace-nowrap"       onClick={() => requestSort('rate')}>Rate <SortIcon column="rate" /></th>
                            <th className="py-4 cursor-pointer whitespace-nowrap"       onClick={() => requestSort('minPayment')}>Min Payment <SortIcon column="minPayment" /></th>
                            <th className="py-4 whitespace-nowrap">Pays Off</th>
                            <th className="py-4 w-[5%] text-right"></th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-inherit">
                          {sortedLoans.map(loan => {
                            const monthlyInterest  = (Number(loan.balance) * Number(loan.rate) / 100) / 12;
                            const minBelowInterest = Number(loan.balance) > 0 && Number(loan.rate) > 0 && Number(loan.minPayment) <= monthlyInterest && !payoffDates[loan.id];
                            const payoffDate       = payoffDates[loan.id];
                            const paidOff          = isPaidOff(loan);
                            return (
                              <tr key={loan.id} className={`group hover:bg-primary-600/5 ${paidOff ? 'bg-summit-500/5' : ''}`}>
                                <td className="py-3 pl-2">
                                  <div className="flex items-center gap-1.5">
                                    {minBelowInterest && <AlertTriangle size={11} className={`${theme.danger} shrink-0`} title="Min payment doesn't cover interest, so this loan won't pay off without extra budget" />}
                                    <input
                                      className="bg-transparent font-bold text-sm outline-none w-full"
                                      value={editingCell.id === loan.id && editingCell.field === 'name' ? editingCell.value : loan.name}
                                      onFocus={(e) => handleFocus(e, loan.id, 'name', loan.name)}
                                      onBlur={() => handleInputBlur(loan.id, 'name')}
                                      onChange={(e) => setEditingCell({ ...editingCell, value: e.target.value })}
                                      onKeyDown={handleEnter}
                                      onPaste={handlePaste}
                                    />
                                  </div>
                                </td>
                                <td className="py-3">
                                  <input
                                    className="bg-transparent font-bold text-sm outline-none w-full"
                                    value={editingCell.id === loan.id && editingCell.field === 'balance' ? editingCell.value : `$${formatCurrency(loan.balance)}`}
                                    onFocus={(e) => handleFocus(e, loan.id, 'balance', loan.balance)}
                                    onBlur={() => handleInputBlur(loan.id, 'balance')}
                                    onChange={(e) => setEditingCell({ ...editingCell, value: e.target.value })}
                                    onKeyDown={handleEnter}
                                    onPaste={handlePaste}
                                  />
                                </td>
                                <td className="py-3">
                                  <input
                                    className={`bg-transparent font-bold text-sm outline-none w-full ${theme.accent}`}
                                    value={editingCell.id === loan.id && editingCell.field === 'rate' ? editingCell.value : `${formatRate(loan.rate)}%`}
                                    onFocus={(e) => handleFocus(e, loan.id, 'rate', loan.rate)}
                                    onBlur={() => handleInputBlur(loan.id, 'rate')}
                                    onChange={(e) => setEditingCell({ ...editingCell, value: e.target.value })}
                                    onKeyDown={handleEnter}
                                    onPaste={handlePaste}
                                  />
                                </td>
                                <td className="py-3">
                                  <input
                                    className="bg-transparent font-bold text-sm outline-none w-full"
                                    value={editingCell.id === loan.id && editingCell.field === 'minPayment' ? editingCell.value : `$${formatCurrency(loan.minPayment)}`}
                                    onFocus={(e) => handleFocus(e, loan.id, 'minPayment', loan.minPayment)}
                                    onBlur={() => handleInputBlur(loan.id, 'minPayment')}
                                    onChange={(e) => setEditingCell({ ...editingCell, value: e.target.value })}
                                    onKeyDown={handleEnter}
                                    onPaste={handlePaste}
                                  />
                                </td>
                                <td className={`py-3 text-[11px] font-bold ${paidOff ? theme.success : theme.muted}`}>
                                  {paidOff ? (
                                    <span className="flex items-center gap-1"><CheckCircle2 size={12} /> Paid off</span>
                                  ) : (
                                    payoffDate ?? <span className={theme.danger}>N/A</span>
                                  )}
                                </td>
                                <td className="py-3 text-right pr-2">
                                  <button onClick={() => setLoans(prev => prev.filter(l => l.id !== loan.id))} className={`text-slate-600 ${theme.dangerHover} opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity -m-2 p-2 rounded-lg active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500/60`}>
                                    <Trash2 size={14} />
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="h-[400px] w-full flex flex-col gap-4">
                <div className="flex items-center justify-between mb-2">
                  <StrategyToggle hint />
                </div>
                <div className="flex-1">
                  {projection.history.length === 0 ? (
                    <div className="h-full flex items-center justify-center">
                      <p className={`${theme.muted} text-sm font-bold text-center`}>
                        {loans.length === 0
                          ? 'Add a debt to see the projection.'
                          : totalDebtBalance === 0
                          ? 'All balances are paid off.'
                          : 'Set a monthly payment above $0 to see the projection.'}
                      </p>
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={projection.history} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <defs>
                          {loans.map((l, i) => (
                            <linearGradient key={`grad-${l.id}`} id={`color-${l.id}`} x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%"  stopColor={getColor(i)} stopOpacity={0.4} />
                              <stop offset="95%" stopColor={getColor(i)} stopOpacity={0}   />
                            </linearGradient>
                          ))}
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={isDarkMode ? '#1e293b' : '#e2e8f0'} />
                        <XAxis dataKey="name" tick={{ fontSize: 11, fontWeight: 700, fill: isDarkMode ? '#94a3b8' : '#475569' }} axisLine={false} tickLine={false} dy={10} minTickGap={45} />
                        <YAxis tickFormatter={v => `$${v.toLocaleString()}`} tick={{ fontSize: 11, fontWeight: 700, fill: isDarkMode ? '#94a3b8' : '#475569' }} axisLine={false} tickLine={false} dx={-10} />
                        <RechartsTooltip
                          contentStyle={{ borderRadius: '16px', border: 'none', backgroundColor: isDarkMode ? '#0f172a' : '#fff', color: isDarkMode ? '#fff' : '#1e293b', fontSize: '11px', boxShadow: '0 4px 24px rgba(0,0,0,0.12)' }}
                          itemStyle={{ padding: '2px 0' }}
                          formatter={(value, name) => value === null ? null : [`$${formatCurrency(value)}`, loans.find(l => l.id === name)?.name || name]}
                        />
                        {loans.map((l, i) => (
                          <Area
                            key={l.id}
                            type="monotone"
                            dataKey={l.id}
                            name={l.name}
                            stroke={getColor(i)}
                            fill={`url(#color-${l.id})`}
                            strokeWidth={2}
                            isAnimationActive={false}
                            connectNulls={false}
                            fillOpacity={0.6}
                          />
                        ))}
                      </AreaChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Libre+Baskerville:ital,wght@0,400;0,700;1,400&family=Inter:wght@400;700;900&display=swap');
        .font-serif { font-family: 'Libre Baskerville', serif; }
        body { font-family: 'Inter', sans-serif; }
        .minimal-slider::-webkit-slider-thumb {
          appearance: none; width: 24px; height: 24px; border-radius: 50%;
          background: currentColor; border: 3px solid white; cursor: pointer;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        }
        .minimal-slider::-moz-range-thumb {
          width: 24px; height: 24px; border-radius: 50%;
          background: currentColor; border: 3px solid white; cursor: pointer;
        }
      `}</style>
    </div>
  );
};

export default App;
